import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ChevronLeft, Download, Lock, Trash2 } from "lucide-react";
import {
  addPatientNote,
  downloadPatientExport,
  getGameSettings,
  getPatientDetail,
  getPatientNotes,
  getPatientSessionsPage,
  patchGameSettings,
  removePatient,
} from "../../../services/CareTeamService";
import {
  formatDate,
  formatMonthYear,
  formatPlayTime,
  formatScore,
  formatWhen,
} from "../careUtils";
import { BarChart, LineChart } from "../charts";
import {
  Dialog,
  StatusBadge,
  Stepper,
  Toast,
  Toggle,
  icon,
  useToast,
} from "../ui";

const SESSIONS_PAGE = 8;
const MAX_START_LEVEL = 20;

// Each setting is automatic (the game's own behaviour) until a provider
// overrides it; only overrides are stored. Session length and a countdown
// timer aren't offered yet: no game has a time-based session or time limit.
export const effectiveSettings = (overrides, currentLevel) => ({
  start_level: overrides.start_level ?? currentLevel ?? 1,
  adaptive: overrides.adaptive !== false,
});

const lastScore = (game) => {
  const scored = game.weeks.filter((w) => w.score !== null);
  return scored.length ? scored[scored.length - 1].score : null;
};

const headerMeta = (p) =>
  [
    p.age !== null && p.age !== undefined ? `Age ${p.age}` : null,
    // So the provider can help them sign in during a visit.
    p.username ? `Username ${p.username}` : null,
    `Joined MendGrove ${formatMonthYear(p.joined_at)}`,
    p.connected_since ? `Connected to you since ${formatDate(p.connected_since)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

const otherProvidersLine = (others) =>
  others.length
    ? "Also connected to " +
      others
        .map((o) => (o.profession_label ? `${o.name}, ${o.profession_label}` : o.name))
        .join("; ")
    : "No other providers";

const SettingSource = ({ overridden, automaticText, overriddenText, onReset }) =>
  overridden ? (
    <span className="ct-setting__source ct-setting__source--override">
      {overriddenText} ·{" "}
      <button type="button" className="ct-link-button" onClick={onReset}>
        Reset to automatic
      </button>
    </span>
  ) : (
    <span className="ct-setting__source">{automaticText}</span>
  );

const GameSettingsCard = ({ patientId, firstName, game, onLevelSet, onError }) => {
  const [overrides, setOverrides] = useState(null);
  const gameId = game.game_id;

  useEffect(() => {
    let live = true;
    setOverrides(null);
    getGameSettings(patientId, gameId)
      .then((stored) => live && setOverrides(stored))
      .catch(() => live && setOverrides({}));
    return () => {
      live = false;
    };
  }, [patientId, gameId]);

  // Save each change straight away; put it back if the save fails. A null
  // value removes the override, returning that setting to automatic.
  const change = async (key, value) => {
    const before = overrides;
    const next = { ...overrides, [key]: value };
    if (value === null) delete next[key];
    setOverrides(next);
    try {
      await patchGameSettings(patientId, gameId, { [key]: value });
      if (key === "start_level" && value !== null) onLevelSet(gameId, value);
    } catch (err) {
      setOverrides(before);
      onError("That change wasn't saved. Please try again.");
    }
  };

  const maxLevel = Math.min(MAX_START_LEVEL, game.level_count || MAX_START_LEVEL);
  const settings = overrides && effectiveSettings(overrides, game.current_level);

  return (
    <section className="ct-card" style={{ gap: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h2>{game.name} settings</h2>
        <p className="ct-card-sub">
          Automatic unless you change them. Changes apply the next time {firstName}{" "}
          plays.
        </p>
      </div>
      {!settings ? (
        <p className="ct-muted">Loading settings…</p>
      ) : (
        <>
          <div className="ct-setting">
            <div className="ct-setting__label">
              <span>Starting level</span>
              <SettingSource
                overridden={overrides.start_level !== undefined}
                automaticText={
                  game.current_level
                    ? `Automatic · currently level ${game.current_level}`
                    : "Automatic · starts at level 1"
                }
                overriddenText={`Set by you · currently level ${
                  game.current_level ?? settings.start_level
                }`}
                onReset={() => change("start_level", null)}
              />
            </div>
            <Stepper
              label="starting level"
              value={settings.start_level}
              min={1}
              max={maxLevel}
              onChange={(v) => change("start_level", v)}
            />
          </div>
          <div className="ct-setting">
            <div className="ct-setting__label">
              <span>Adjust difficulty automatically</span>
              <span className="ct-setting__hint">
                Moves up or down a level based on recent scores
              </span>
              {overrides.adaptive === false && (
                <SettingSource
                  overridden
                  overriddenText="Turned off by you"
                  onReset={() => change("adaptive", null)}
                />
              )}
            </div>
            <Toggle
              label="Adjust difficulty automatically"
              checked={settings.adaptive}
              // On is the automatic behaviour, so switching on clears the override.
              onChange={(on) => change("adaptive", on ? null : false)}
            />
          </div>
        </>
      )}
    </section>
  );
};

const SessionsTable = ({ patientId }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadMore = useCallback(
    async (offset) => {
      setBusy(true);
      try {
        const page = await getPatientSessionsPage(patientId, offset, SESSIONS_PAGE);
        setRows((rs) => (offset ? [...rs, ...page.results] : page.results));
        setTotal(page.total);
      } finally {
        setBusy(false);
      }
    },
    [patientId],
  );

  useEffect(() => {
    loadMore(0).catch(() => setTotal(0));
  }, [loadMore]);

  return (
    <section className="ct-section">
      <h2 style={{ fontSize: 28 }}>Recent sessions</h2>
      {total === 0 ? (
        <p className="ct-muted">No sessions yet.</p>
      ) : (
        <div className="ct-table-wrap">
          <table className="ct-table ct-sessions" style={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th scope="col">Date and time</th>
                <th scope="col">Game</th>
                <th scope="col" className="num">
                  Level
                </th>
                <th scope="col" className="num">
                  Score
                </th>
                <th scope="col" className="num">
                  Duration
                </th>
                <th scope="col">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td className="tnum">{formatWhen(s.started_at)}</td>
                  <td>{s.game}</td>
                  <td className="num">{s.level ?? "—"}</td>
                  <td className="num">{formatScore(s.score)}</td>
                  <td className="num">{formatPlayTime(s.play_seconds)}</td>
                  <td>
                    <span
                      className={s.completed ? "ct-outcome--done" : "ct-outcome--stopped"}
                    >
                      {s.completed ? "Completed" : "Stopped early"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total !== null && rows.length < total && (
        <div>
          <button
            type="button"
            className="ct-btn ct-btn--secondary"
            disabled={busy}
            onClick={() => loadMore(rows.length)}
          >
            Show more
          </button>
        </div>
      )}
    </section>
  );
};

const NotesPanel = ({ patientId, onSaved, onError }) => {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPatientNotes(patientId)
      .then(setNotes)
      .catch(() => setNotes([]));
  }, [patientId]);

  const save = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const note = await addPatientNote(patientId, draft.trim());
      setNotes((ns) => [note, ...ns]);
      setDraft("");
      onSaved();
    } catch (err) {
      onError("Your note wasn't saved. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ct-card">
      <div className="ct-card-head" style={{ gap: 8 }}>
        <h2>Clinician notes</h2>
        <span className="ct-private">
          {icon(Lock)}
          Private to you. The patient and other providers can't see these.
        </span>
      </div>
      <label htmlFor="ct-note" className="ct-sr-only">
        New note
      </label>
      <textarea
        id="ct-note"
        className="ct-input"
        placeholder="Add a note about this patient's progress…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div>
        <button
          type="button"
          className="ct-btn ct-btn--primary"
          style={{ minHeight: 44 }}
          onClick={save}
          disabled={busy || !draft.trim()}
        >
          Save note
        </button>
      </div>
      <div>
        {notes.map((n) => (
          <div key={n.id} className="ct-note">
            <span className="ct-note__date">{formatDate(n.created_at)}</span>
            <p>{n.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// /provider/patients/:id
const PatientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [missing, setMissing] = useState(false);
  const [gameIndex, setGameIndex] = useState(0);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [toast, showToast] = useToast();

  useEffect(() => {
    setPatient(null);
    setMissing(false);
    setGameIndex(0);
    getPatientDetail(id)
      .then(setPatient)
      .catch(() => setMissing(true));
  }, [id]);

  const showError = useCallback((text) => showToast(text, { error: true }), [showToast]);

  // A starting-level override moves the patient to that level straight away.
  const setLevel = useCallback((gameId, level) => {
    setPatient((p) => ({
      ...p,
      games: p.games.map((g) =>
        g.game_id === gameId ? { ...g, current_level: level } : g,
      ),
    }));
  }, []);

  if (missing) {
    return (
      <main className="ct-page ct-page--detail">
        <Link to="/provider/patients" className="ct-back">
          {icon(ChevronLeft)}
          All patients
        </Link>
        <p>
          This patient isn't on your roster, or they've stopped sharing their progress
          with you.
        </p>
      </main>
    );
  }
  if (!patient) {
    return (
      <main className="ct-page ct-page--detail">
        <p className="ct-loading">Loading…</p>
      </main>
    );
  }

  const game = patient.games[gameIndex];
  const first = patient.first_name || patient.name.split(" ")[0];
  const flagged = patient.status === "inactive";

  const exportCsv = async () => {
    try {
      await downloadPatientExport(patient.id);
      showToast("CSV exported");
    } catch (err) {
      showError("The export didn't work. Please try again.");
    }
  };

  const remove = async () => {
    try {
      await removePatient(patient.id);
      navigate("/provider/patients", {
        state: { toast: `${patient.name} removed from your roster` },
      });
    } catch (err) {
      setConfirmRemove(false);
      showError(`We couldn't remove ${patient.name}. Please try again.`);
    }
  };

  return (
    <main className="ct-page ct-page--detail">
      <Helmet>
        <title>{patient.name} – MendGrove for clinicians</title>
      </Helmet>
      <Link to="/provider/patients" className="ct-back">
        {icon(ChevronLeft)}
        All patients
      </Link>
      <header className="ct-detail-head">
        <div className="ct-detail-head__info">
          <div className="ct-detail-head__name">
            <h1>{patient.name}</h1>
            <StatusBadge status={patient.status} />
          </div>
          <p className="ct-detail-head__meta">{headerMeta(patient)}</p>
          <p className="ct-detail-head__also">
            {otherProvidersLine(patient.other_providers)}
          </p>
          {flagged && (
            <span className="ct-inactive-notice">
              {icon(AlertTriangle)}
              {patient.days_since_active === null
                ? "No sessions yet since connecting."
                : `No activity in ${patient.days_since_active} days.`}
            </span>
          )}
        </div>
        <div className="ct-actions">
          <button type="button" className="ct-btn ct-btn--secondary" onClick={exportCsv}>
            {icon(Download)}
            Export CSV
          </button>
          <button
            type="button"
            className="ct-btn ct-btn--danger"
            onClick={() => setConfirmRemove(true)}
          >
            {icon(Trash2)}
            Remove patient
          </button>
        </div>
      </header>

      {game ? (
        <>
          <section className="ct-section">
            <h2 className="ct-section-label">Current level by game</h2>
            <div className="ct-game-tabs">
              {patient.games.map((g, i) => {
                const avg = lastScore(g);
                return (
                  <button
                    key={g.game_id}
                    type="button"
                    className="ct-game-tab"
                    aria-pressed={i === gameIndex}
                    onClick={() => setGameIndex(i)}
                  >
                    <span className="ct-game-tab__name">{g.name}</span>
                    <span className="ct-game-tab__meta">
                      Level {g.current_level ?? "—"} ·{" "}
                      {avg === null ? "no scores yet" : `avg ${formatScore(avg)}`}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="ct-card">
            <div className="ct-card-head">
              <h2>{game.name}: progress over 10 weeks</h2>
              <div className="ct-legend" aria-hidden="true">
                <span>
                  <span className="ct-legend__score" />
                  Average score (left)
                </span>
                <span>
                  <span className="ct-legend__level" />
                  Level (right)
                </span>
              </div>
            </div>
            <LineChart
              weeks={game.weeks}
              title={`${game.name}: average score and level by week`}
            />
          </section>

          <div className="ct-grid-2">
            <section className="ct-card">
              <h2>Sessions per week</h2>
              <p className="ct-card-sub">All games, last 8 weeks</p>
              <BarChart weeks={patient.sessions_per_week} />
            </section>
            <GameSettingsCard
              patientId={patient.id}
              firstName={first}
              game={game}
              onLevelSet={setLevel}
              onError={showError}
            />
          </div>
        </>
      ) : (
        <section className="ct-card">
          <h2>No games played yet</h2>
          <p className="ct-muted">
            Progress, charts and game settings will appear here after {first} plays.
          </p>
        </section>
      )}

      <SessionsTable patientId={patient.id} />
      <NotesPanel
        patientId={patient.id}
        onSaved={() => showToast("Note saved")}
        onError={showError}
      />

      {confirmRemove && (
        <Dialog
          role="alertdialog"
          labelledBy="ct-remove-title"
          className="ct-dialog ct-dialog--narrow"
          onClose={() => setConfirmRemove(false)}
        >
          <h2 id="ct-remove-title" className="ct-dialog__title">
            Remove {patient.name}?
          </h2>
          <p style={{ color: "var(--color-neutral-800)" }}>
            You'll stop seeing {first}'s progress and your settings for them will be
            cleared. Their account, game history and other providers are not affected.
            Your private notes are kept for your records.
          </p>
          <div className="ct-actions">
            <button
              type="button"
              className="ct-btn ct-btn--secondary"
              onClick={() => setConfirmRemove(false)}
            >
              Keep patient
            </button>
            <button
              type="button"
              className="ct-btn ct-btn--danger"
              style={{ borderColor: "var(--mg-clay-danger-strong)" }}
              onClick={remove}
            >
              Remove patient
            </button>
          </div>
        </Dialog>
      )}
      <Toast toast={toast} />
    </main>
  );
};

export default PatientDetailPage;
